import { ReactNode } from "react";
import { toast, ToastIcon } from "react-toastify";

import {
  CircleCheckIcon,
  CircleCross,
  SpinnerIcon,
  WarningIcon
} from "@/assets/icons";

import { twMerge } from "tailwind-merge";

export enum EToastType {
  LOADING = "loading",
  SUCCESS = "success",
  ERROR = "error",
  WARNING = "warning"
}

type ToastType = "loading" | "error" | "success" | "warning";

interface ToastOptions {
  message: string | ReactNode;
  type: ToastType;
  icon?: ToastIcon;
  autoClose?: number | false; // Auto close time, or false to keep it open
  className?: string;
  onClose?: (reason?: boolean | string) => void;
  id?: string;
}

const useToast = () => {
  const showToast = ({
    message,
    type,
    icon,
    autoClose,
    className = "",
    onClose,
    id
  }: ToastOptions) => {
    switch (type) {
      case EToastType.LOADING: {
        toast.dismiss();
        return toast.loading(message as string, {
          ...(id && { toastId: id }),
          className: twMerge("toast-custom toast-loading", className),
          icon: icon ?? <SpinnerIcon />,
          autoClose: autoClose ?? false
        });
      }
      case EToastType.ERROR: {
        toast.dismiss();
        return toast.error(message, {
          ...(id && { toastId: id }),
          className: twMerge("toast-custom toast-error", className),
          icon: icon || <CircleCross className="fill-cinnabar" />,
          autoClose: autoClose ?? 3000,
          onClose
        });
      }
      case EToastType.SUCCESS: {
        toast.dismiss();
        return toast.success(message as string, {
          ...(id && { toastId: id }),
          className: twMerge("toast-custom toast-success", className),
          icon: icon || <CircleCheckIcon className="fill-shamrock-green" />,
          autoClose: autoClose ?? 3000,
          onClose
        });
      }
      case EToastType.WARNING: {
        toast.dismiss();
        return toast.warning(message as string, {
          ...(id && { toastId: id }),
          className: twMerge("toast-custom toast-warning", className),
          icon: icon || <WarningIcon className="fill-yellow-600" />,
          autoClose: autoClose ?? 3000
        });
      }
    }
  };

  return { showToast };
};

export default useToast;
